import React from 'react';

/**
 * TransactionProgressOverlay
 * 
 * Shows a prominent overlay during transaction processing with:
 * - Visual step indicator (1/2, 2/2)
 * - Animated spinner
 * - Clear status messages
 * - Post-signature "return to XCoin" guidance
 *
 * Props:
 *   visible: boolean
 *   phase: 'waiting-wallet' | 'mining' | 'confirmed' | 'error'
 *   currentStep: number (1 or 2)
 *   totalSteps: number (1 or 2)
 *   message: string (descriptive status)
 *   txHash: string | null
 *   errorMsg: string | null
 *   onDismiss: function (only for error/confirmed states)
 */
const TransactionProgressOverlay = ({ visible, phase, currentStep, totalSteps, message, txHash, errorMsg, onDismiss }) => {
  if (!visible) return null;

  const isWaiting = phase === 'waiting-wallet';
  const isMining = phase === 'mining';
  const isConfirmed = phase === 'confirmed';
  const isError = phase === 'error';

  // Step indicators
  const steps = [];
  for (let i = 1; i <= totalSteps; i++) {
    let status = 'pending';
    if (i < currentStep) status = 'done';
    else if (i === currentStep) {
      if (isConfirmed) status = 'done';
      else if (isError) status = 'error';
      else status = 'active';
    }
    
    const label = totalSteps === 2
      ? (i === 1 ? 'Aprobación' : 'Transacción')
      : 'Transacción';

    steps.push({ number: i, label, status });
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        background: '#1a1a2e',
        borderRadius: '16px',
        padding: '30px 25px',
        maxWidth: '400px',
        width: '100%',
        textAlign: 'center',
        border: isError ? '1px solid #ff4444' : '1px solid rgba(0,210,255,0.3)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }}>

        {/* Step Indicators */}
        {totalSteps > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '25px',
          }}>
            {steps.map((step, idx) => (
              <React.Fragment key={step.number}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '6px',
                }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    background: step.status === 'done' ? '#4caf50'
                      : step.status === 'active' ? '#00d2ff'
                      : step.status === 'error' ? '#ff4444'
                      : '#333',
                    color: step.status === 'active' ? '#000' : '#fff',
                    transition: 'all 0.3s ease',
                    ...(step.status === 'active' ? {
                      boxShadow: '0 0 15px rgba(0,210,255,0.5)',
                      animation: 'pulse-step 2s infinite',
                    } : {}),
                  }}>
                    {step.status === 'done' ? '✓' : step.number}
                  </div>
                  <span style={{
                    fontSize: '0.7rem',
                    color: step.status === 'done' ? '#4caf50'
                      : step.status === 'active' ? '#00d2ff'
                      : '#666',
                    fontWeight: step.status === 'active' ? 'bold' : 'normal',
                  }}>
                    {step.label}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div style={{
                    width: '40px',
                    height: '2px',
                    background: step.status === 'done' ? '#4caf50' : '#333',
                    marginBottom: '20px',
                    transition: 'background 0.3s ease',
                  }} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Animated Icon */}
        <div style={{ marginBottom: '20px' }}>
          {isWaiting && (
            <div style={{ fontSize: '3rem', animation: 'bounce-wallet 1.5s infinite' }}>
              Wallet
            </div>
          )}
          {isMining && (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <div style={{
                width: '60px',
                height: '60px',
                border: '4px solid #333',
                borderTop: '4px solid #00d2ff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto',
              }} />
              <div style={{
                position: 'absolute',
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '1.5rem',
              }}>TX</div>
            </div>
          )}
          {isConfirmed && (
            <div style={{
              fontSize: '3rem',
              animation: 'pop-in 0.5s ease',
            }}>✅</div>
          )}
          {isError && (
            <div style={{ fontSize: '3rem' }}>❌</div>
          )}
        </div>

        {/* Main Message */}
        <h3 style={{
          color: isError ? '#ff4444' : isConfirmed ? '#4caf50' : '#fff',
          margin: '0 0 10px 0',
          fontSize: '1.1rem',
        }}>
          {isWaiting && 'Confirma en tu Wallet'}
          {isMining && 'Procesando en Blockchain'}
          {isConfirmed && '¡Transacción Confirmada!'}
          {isError && 'Error en Transacción'}
        </h3>

        {/* Descriptive Message */}
        <p style={{
          color: '#aaa',
          fontSize: '0.9rem',
          margin: '0 0 15px 0',
          lineHeight: '1.4',
        }}>
          {message}
        </p>

        {/* Contextual guidance */}
        {isWaiting && (
          <div style={{
            background: 'rgba(0,210,255,0.1)',
            border: '1px solid rgba(0,210,255,0.2)',
            borderRadius: '8px',
            padding: '12px',
            marginTop: '10px',
          }}>
            <p style={{ color: '#00d2ff', fontSize: '0.8rem', margin: 0 }}>
              Nota: Abre tu wallet y aprueba la transaccion. Luego vuelve aqui, te avisaremos cuando se confirme.
            </p>
          </div>
        )}

        {isMining && (
          <div style={{
            background: 'rgba(255,215,0,0.1)',
            border: '1px solid rgba(255,215,0,0.2)',
            borderRadius: '8px',
            padding: '12px',
            marginTop: '10px',
          }}>
            <p style={{ color: '#ffd700', fontSize: '0.8rem', margin: '0 0 6px 0' }}>
              ⏳ Esperando confirmación de la red... Puedes quedarte aquí o volver luego.
            </p>
            {txHash && (
              <a
                href={`https://bscscan.com/tx/${txHash}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#00d2ff', fontSize: '0.75rem', wordBreak: 'break-all' }}
              >
                Ver en BscScan →
              </a>
            )}
          </div>
        )}

        {isConfirmed && totalSteps > 1 && currentStep < totalSteps && (
          <div style={{
            background: 'rgba(76,175,80,0.1)',
            border: '1px solid rgba(76,175,80,0.3)',
            borderRadius: '8px',
            padding: '12px',
            marginTop: '10px',
          }}>
            <p style={{ color: '#4caf50', fontSize: '0.85rem', margin: 0 }}>
              ✅ Paso {currentStep} completado. Continúa con el Paso {currentStep + 1}.
            </p>
          </div>
        )}

        {isError && (
          <div style={{
            background: 'rgba(255,68,68,0.1)',
            borderRadius: '8px',
            padding: '12px',
            marginTop: '10px',
          }}>
            <p style={{ color: '#ff6666', fontSize: '0.8rem', margin: 0, wordBreak: 'break-word' }}>
              {errorMsg || 'Error desconocido'}
            </p>
          </div>
        )}

        {/* Dismiss button for error/confirmed */}
        {(isError || (isConfirmed && currentStep >= totalSteps)) && onDismiss && (
          <button
            onClick={onDismiss}
            style={{
              marginTop: '20px',
              padding: '12px 30px',
              borderRadius: '8px',
              border: 'none',
              background: isError ? '#ff4444' : '#4caf50',
              color: '#fff',
              fontWeight: 'bold',
              fontSize: '0.95rem',
              cursor: 'pointer',
            }}
          >
            {isError ? 'Cerrar' : 'Continuar'}
          </button>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes pulse-step {
          0%, 100% { box-shadow: 0 0 10px rgba(0,210,255,0.3); }
          50% { box-shadow: 0 0 25px rgba(0,210,255,0.6); }
        }
        @keyframes bounce-wallet {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes pop-in {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default TransactionProgressOverlay;
